import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { createGlobalStyle, ThemeProvider } from 'styled-components';
import { Header } from './components/common/Header';
import { HomePage } from './pages/HomePage';
import { StoryLibraryPage } from './pages/StoryLibraryPage';
import { CreateStoryPage } from './pages/CreateStoryPage';
import { ReadStoryPage } from './pages/ReadStoryPage';
import { ProfilePage } from './pages/ProfilePage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import GoogleCallback from './pages/GoogleCallback';
import ConsentModal from './components/ConsentModal';
import { ContactPage } from './pages/ContactPage';
import { LegalNoticePage } from './pages/LegalNoticePage';
import { TermsOfServicePage } from './pages/TermsOfServicePage';
import { PrivacyPolicyPage } from './pages/PrivacyPolicyPage';
import { BookCheckout } from './pages/BookCheckout';
import { OrderSuccess } from './pages/OrderSuccess';
import { AdminOrders } from './pages/AdminOrders';
import BlogList from './pages/BlogList';
import BlogPost from './pages/BlogPost';
import { CartProvider } from './context/CartContext';
import { CartPage } from './pages/CartPage';
import { initPixel, revokePixel, trackPageView } from './utils/metaPixel';
import LandingPage from './pages/LandingPage';
import { ConsentProvider, useConsent, useMarketingConsentEffect } from './context/ConsentContext';
import { CookieBanner } from './components/CookieBanner';

const SUPPORTED_LANGS = ['en', 'ro', 'de', 'fr', 'es', 'it'];

const GlobalStyle = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  
  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    background-color: #f8f9fa;
    color: #212529;
    line-height: 1.5;
  }
  
  button, input, select, textarea {
    font-family: inherit;
  }
`;

const theme = {
  colors: {
    primary: '#4263eb',
    secondary: '#e9ecef',
    danger: '#fa5252',
    success: '#40c057',
    text: {
      primary: '#212529',
      secondary: '#495057',
      muted: '#868e96',
    },
    background: {
      main: '#f8f9fa',
      paper: '#ffffff',
    },
  },
  breakpoints: {
    xs: '0px',
    sm: '576px',
    md: '768px',
    lg: '992px',
    xl: '1200px',
  },
};

// Footer component for global use
const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { openBanner } = useConsent();

  return (
    <footer style={{
      backgroundColor: '#fff',
      padding: '24px',
      marginTop: '48px',
      borderTop: '1px solid #dee2e6',
      textAlign: 'center',
      color: '#868e96',
      fontSize: '0.875rem'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <p>© {new Date().getFullYear()} {t('footer.copyright')}</p>
        <p style={{ marginTop: '8px' }}>
          {t('footer.tagline')}
        </p>
        
        {/* Legal Links */}
        <div style={{ 
          marginTop: '16px', 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px',
          flexWrap: 'wrap'
        }}>
          <Link to="/contact" style={{ color: '#868e96', textDecoration: 'none' }}>
            {t('footer.contact')}
          </Link>
          <Link to="/privacy" style={{ color: '#868e96', textDecoration: 'none' }}>
            {t('footer.privacy')}
          </Link>
          <Link to="/terms" style={{ color: '#868e96', textDecoration: 'none' }}>
            {t('footer.terms')}
          </Link>
          {/* Consent must be as easy to withdraw as it was to give */}
          <button
            onClick={openBanner}
            style={{
              color: '#868e96',
              background: 'none',
              border: 'none',
              padding: 0,
              font: 'inherit',
              cursor: 'pointer',
            }}
          >
            {t('footer.cookieSettings', 'Cookie settings')}
          </button>
          {/* <Link to="/legal" style={{ color: '#868e96', textDecoration: 'none' }}>
            {t('footer.legal')}
          </Link> */}
        </div>
      </div>
    </footer>
  );
};

const AppContent: React.FC = () => {
  const { isLoggedIn, user } = useAuth();
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle language prefix URLs (e.g. /ro, /fr, /de)
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];
    
    // Only act if the URL is exactly /{lang} (no deeper path)
    // Blog routes like /:lang/blog are handled by their own routes
    if (firstSegment && SUPPORTED_LANGS.includes(firstSegment) && pathSegments.length === 1) {
      i18n.changeLanguage(firstSegment);
      localStorage.setItem('preferredLanguage', firstSegment);
      navigate('/', { replace: true });
    }
  }, [location.pathname]);

  /*
   * The pixel is no longer started on mount. initPixel() is what injects
   * fbevents.js and sets _fbp, so it runs only once marketing consent is
   * granted — and is revoked the moment that consent is withdrawn.
   *
   * Declared before the PageView effect below so that, on the render where
   * consent is granted, the pixel is live by the time PageView fires.
   */
  useMarketingConsentEffect(initPixel, revokePixel);

  /*
   * PageView on every route change, not just the first load — this is an
   * SPA, so the old index.html script only ever counted the landing page.
   * `consent` is a dependency so the current page is still counted when
   * someone accepts without navigating. No-ops until the pixel is live.
   */
  const { consent } = useConsent();
  useEffect(() => {
    trackPageView();
  }, [location.pathname, consent]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Show consent modal if user is logged in but hasn't consented */}
      {isLoggedIn && user && !user.ageConsent && <ConsentModal />}
      
      <Header />
      <CookieBanner />

      <main style={{ flex: 1 }}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/library" element={<StoryLibraryPage />} />
          <Route path="/create" element={<CreateStoryPage />} />
          <Route path="/story/:storyId" element={<ReadStoryPage />} />
          <Route path="/story/:storyId/read" element={<ReadStoryPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/auth/google/callback" element={<GoogleCallback />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/legal" element={<LegalNoticePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/book/checkout" element={<BookCheckout />} />
          <Route path="/order/success" element={<OrderSuccess />} />
          <Route path="/admin/orders" element={<AdminOrders />} />
          <Route path="/cart" element={<CartPage />} /> 
          <Route path="/promo/:country" element={<LandingPage />} />

          {/* English blog routes */}
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />

          {/* Other languages blog routes */}
          <Route path="/:lang/blog" element={<BlogList />} />
          <Route path="/:lang/blog/:slug" element={<BlogPost />} />

          {/* keep this always last */}
          <Route path="*" element={<Navigate to="/" replace />} /> 
        </Routes>
      </main>
      <Footer />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Router>
        <ConsentProvider>
          <AuthProvider>
            <CartProvider>
              <AppContent />
            </CartProvider>
          </AuthProvider>
        </ConsentProvider>
      </Router>
    </ThemeProvider>
  );
};

export default App;